/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useApolloClient } from '@apollo/client/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginProps } from '..';

type OidcCallbackHandlerProps = Pick<
  LoginProps,
  'pathPrefix' | 'oidcTokenUrl' | 'postLoginPath'
>;

export const OidcCallbackHandler: React.FC<OidcCallbackHandlerProps> = ({
  pathPrefix,
  oidcTokenUrl,
  postLoginPath = '/',
}) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  const fetchToken = React.useCallback(async () => {
    const errorParam = new URLSearchParams(window.location.search).get('error');
    if (errorParam) {
      setError(errorParam);
      setIsLoading(false);
      return;
    }

    if (!oidcTokenUrl) {
      setError('Authentication configuration error. Please contact support.');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Add a timeout to prevent hanging indefinitely
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 15000),
      );

      const fetchPromise = fetch(oidcTokenUrl, {
        credentials: 'include', // Ensures cookies are sent
      });

      // Race between fetch and timeout
      const response = (await Promise.race([
        fetchPromise,
        timeoutPromise,
      ])) as Response;

      if (!response.ok) {
        // Try to get error details from response if possible
        let errorMessage = `Authentication failed (${response.status})`;
        try {
          const errorData = await response.json();
          if (errorData?.message) {
            errorMessage = errorData.message;
          } else if (errorData?.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // If can't parse JSON, use status text
          errorMessage = `Authentication failed: ${
            response.statusText || 'Server error'
          }`;
        }

        throw new Error(errorMessage);
      }

      const json = await response.json();

      if (json.accessToken && json.accessToken.length > 10) {
        localStorage.setItem('token', json.accessToken);
        // Store the refresh token if it exists
        if (json.refreshToken) {
          localStorage.setItem('refreshToken', json.refreshToken);
        } else {
          console.warn('[OIDC] No refresh token was returned from the server');
        }
        await client.resetStore();
        await client.cache.reset();
        navigate(postLoginPath, { replace: true });
      } else {
        throw new Error('Invalid authentication response. Please try again.');
      }
    } catch (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      if (error instanceof Error) {
        if (error.message === 'Request timeout') {
          errorMessage = 'Authentication request timed out. Please try again.';
        } else {
          errorMessage = error.message;
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        // Use type assertion to tell TypeScript about the structure
        errorMessage = String((error as { message: unknown }).message);
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [client, oidcTokenUrl, navigate, postLoginPath]);

  React.useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  // Show appropriate UI based on state
  if (isLoading) {
    return (
      <div className="oidc-callback-container">
        <div className="spinner"></div>
        <p>Redirecting, please wait...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="oidc-callback-error">
        <h3>Authentication Error</h3>
        <p>{error}</p>
        <button
          onClick={() => navigate(pathPrefix, { replace: true })}
          className="retry-button"
        >
          Return to Login
        </button>
      </div>
    );
  }

  // This will only show briefly before redirect happens
  return <span>Authentication successful. Redirecting...</span>;
};
